import { GridApi } from '@ag-grid-community/core';
import { AsyncPipe, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import dayjs from 'dayjs';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';

import { ExchangeLinkComponent } from '@features/exchange-link/exchange-link.component';
import { ExportExcelButtonComponent } from '@features/export-excel-button/export-excel-button.component';

import { CardComponent } from '@shared/components/card/card.component';
import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import { MoneyPipe } from '@shared/pipes/money.pipe';
import { EntityType, EventType, WorkflowStep } from '@shared/services/gql.service';
import { TransactionMode } from '@shared/services/investigator-transactions/investigator-transaction-form.model';
import { OverlayService } from '@shared/services/overlay.service';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';
import { AgSetColumnsVisible } from '@shared/utils';
import { ExportType } from '@shared/utils/utils';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CardComponent,
    CheckboxComponent,
    ExchangeLinkComponent,
    ExportExcelButtonComponent,
    MoneyPipe,
    AsyncPipe,
    DatePipe,
  ],
  selector: 'aux-investigator-transactions-header',
  templateUrl: './investigator-transactions-header.component.html',
})
export class InvestigatorTransactionsHeaderComponent {
  private eventService = inject(EventService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);
  private workflowQuery = inject(WorkflowQuery);

  btnLoading$ = new BehaviorSubject<'export' | false>(false);
  @Input({ required: true }) dataStreamInputs?: Record<string, unknown>;
  @Input() editMode$!: BehaviorSubject<TransactionMode>;
  @Input({ required: true }) gridAPI!: GridApi;
  @Input({ required: true }) isContractSelected$!: BehaviorSubject<boolean>;
  isPatientsFinalized = this.workflowQuery.getLockStatusByWorkflowStepType(
    WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_PATIENT_TRACKER
  );
  @Input({ required: true }) lastSourceRefreshDate$!: BehaviorSubject<string>;
  @Input({ required: true }) totalCost$!: BehaviorSubject<number>;

  isBtnLoading(str: string) {
    return this.btnLoading$.pipe(map((x) => x === str));
  }

  async onExportInvestigatorTransactions() {
    const trialName = this.mainQuery.getSelectedTrial()?.short_name || '';
    const dateStr = dayjs(new Date()).format('YYYY.MM.DD-HHmmss');

    if (this.btnLoading$.getValue()) {
      return;
    }

    this.btnLoading$.next('export');

    const { errors, success } = await firstValueFrom(
      this.eventService.processEvent$({
        entity_id: this.mainQuery.getSelectedTrial()?.id || '',
        entity_type: EntityType.TRIAL,
        payload: JSON.stringify({
          export_type: ExportType.INVESTIGATOR_TRANSACTIONS,
          filename: `${trialName}_auxilius-investigator-transactions__${dateStr}`,
          filter_model: this.dataStreamInputs?.filter_model || [],
          sort_model: this.dataStreamInputs?.sort_model || [],
        }),
        type: EventType.GENERATE_EXPORT,
      })
    );

    if (success) {
      this.overlayService.success(
        'Export is being generated and will download when complete. You may leave the page.'
      );
    } else {
      this.overlayService.error(errors);
    }

    this.btnLoading$.next(false);
  }

  toggleContractCurrency(selected: boolean) {
    AgSetColumnsVisible({
      gridApi: this.gridAPI,
      keys: ['contract_curr', 'contract_amount', 'exchange_rate'],
      visible: selected,
    });
    this.isContractSelected$.next(selected);
  }
}
